import React, { useState, useEffect, useRef } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Table from "../../../components/common/Table/Table";
import { getApiInstance } from "../../../utils/api";
import { ZONE_LABEL_MAP, ZONE_OPTIONS } from "../../../constants/zones";
import { useTranslation } from "react-i18next";
import { FaUserEdit } from "react-icons/fa";
import Badge from "../../../components/common/Badge/Badge";
import { MdVisibility as MdVisibilityIcon } from "react-icons/md";
import Button from "../../../components/common/Button/Button";
import { IoMdAdd } from "react-icons/io";

// Misma política que aplica el backend en Utils/PasswordPolicy.cs. Aquí solo sirve
// para dar un mensaje inmediato: la validación que manda es la del servidor.
const validarPassword = (password) => {
    if (!password || password.length < 8) {
        return "La contraseña debe tener al menos 8 caracteres.";
    }
    if (!/[A-Z]/.test(password)) {
        return "La contraseña debe contener al menos una letra mayúscula.";
    }
    if (!/[a-z]/.test(password)) {
        return "La contraseña debe contener al menos una letra minúscula.";
    }
    if (!/[0-9]/.test(password)) {
        return "La contraseña debe contener al menos un número.";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        return "La contraseña debe contener al menos un carácter especial.";
    }
    return null;
};

export default function Usuarios({ user }) {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    const canAdd = Number(user.codAcceso) === 1 && Number(user.role) !== 4;
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const emailLabel = t("fields.email");
    const emailPlaceholder = t("placeholders.enterEmail");

    const columnasUsuarios = [
        { key: "id", label: "codUsuario", visible: false },
        { key: "codUsuario", label: "codUsuario", visible: false },
        {
            key: "nombre",
            label: t("fields.name"),
            visible: true,
            sortable: true,
            render: (row) => {
                return (
                    <span className="table-cell-content__primary table-cell-content__primary--bold">
                        {row.nombre}
                    </span>
                );
            },
        },
        {
            key: "apellidos",
            label: t("fields.lastName"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "perfil",
            label: t("fields.profile"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "acceso",
            label: t("fields.access"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
            render: (row) => {
                const variant =
                    row.acceso === "Gestión" ? "primary" : "neutral";
                const Icon =
                    row.acceso === "Gestión" ? FaUserEdit : MdVisibilityIcon;
                return (
                    <Badge variant={variant} Icon={Icon}>
                        {row.acceso}
                    </Badge>
                );
            },
        },
        {
            key: "intermediario",
            label: t("fields.intermediaryCompany"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "telefono",
            label: t("fields.phone"),
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
        {
            key: "email",
            label: emailLabel,
            visible: true,
            sortable: true,
            visibleOnExpand: true,
        },
    ];

    const filterFields = [
        { key: "perfil", label: t("fields.profile"), type: "multiselect" },
        { key: "acceso", label: t("fields.access"), type: "multiselect" },
        {
            key: "intermediario",
            label: t("fields.intermediaryCompany"),
            type: "multiselect",
        },
        { key: "codZona", label: "Zona", type: "multiselect", labelMap: ZONE_LABEL_MAP },
    ];

    useEffect(() => {
        fetchUsuarios();
    }, []);

    const fetchUsuarios = async () => {
        setLoading(true);
        const api = getApiInstance();

        try {
            let usuariosData = [];

            const response = await api.get(
                `/api/Usuario/GetUsuarios`
            );
            usuariosData = response.data;

            console.log("Paises data fetched:", usuariosData);

            setUsuarios(usuariosData);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los usuarios. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const ALL_PROFILE_OPTIONS = [
        { label: t("fields.profileAdministratorZone"), value: 2 },
        { label: t("fields.profileCenterManager"), value: 3 },
        { label: t("fields.profileIntermediary"), value: 4 },
    ];

    const formElements = [
        {
            elements: [
                {
                    name: "email",
                    type: "email",
                    label: emailLabel,
                    placeholder: emailPlaceholder,
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "nombre",
                    type: "text",
                    label: t("fields.name"),
                    placeholder: t("placeholders.enterName"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "apellidos",
                    type: "text",
                    label: t("fields.lastName"),
                    placeholder: t("placeholders.enterLastName"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "telefono",
                    type: "tel",
                    label: t("fields.phone"),
                    placeholder: t("placeholders.enterPhone"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codPerfil",
                    type: "select",
                    label: t("fields.profile"),
                    placeholder: t("placeholders.selectProfile"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: ALL_PROFILE_OPTIONS,
                },
                {
                    name: "codAcceso",
                    type: "select",
                    label: t("fields.access"),
                    placeholder: t("placeholders.selectAccess"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: [
                        { label: t("fields.accessManagemenet"), value: 1 },
                        { label: t("fields.accessConsulting"), value: 2 },
                    ],
                },
                {
                    // El nombre tiene que ser "password": es como liga con UsuarioDTO
                    // en el backend. Con "contraseña" no ligaba y los usuarios se
                    // creaban sin contraseña.
                    name: "password",
                    type: "password",
                    label: "Contraseña",
                    placeholder: "Ingrese la contraseña",
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "repetirPassword",
                    type: "password",
                    label: "Repetir Contraseña",
                    placeholder: "Repita la contraseña",
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codZona",
                    type: "select",
                    label: "Zona",
                    placeholder: "Selecciona la zona",
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: ZONE_OPTIONS,
                },
            ],
        },
    ];

    const editFormElements = [
        {
            elements: [
                {
                    name: "email",
                    type: "email",
                    label: emailLabel,
                    placeholder: emailPlaceholder,
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "nombre",
                    type: "text",
                    label: t("fields.name"),
                    placeholder: t("placeholders.enterName"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "apellidos",
                    type: "text",
                    label: t("fields.lastName"),
                    placeholder: t("placeholders.enterLastName"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "telefono",
                    type: "tel",
                    label: t("fields.phone"),
                    placeholder: t("placeholders.enterPhone"),
                    required: false,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                },
                {
                    name: "codPerfil",
                    type: "select",
                    label: t("fields.profile"),
                    placeholder: t("placeholders.selectProfile"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: ALL_PROFILE_OPTIONS,
                },
                {
                    name: "codAcceso",
                    type: "select",
                    label: t("fields.access"),
                    placeholder: t("placeholders.selectAccess"),
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: [
                        { label: t("fields.accessManagemenet"), value: 1 },
                        { label: t("fields.accessConsulting"), value: 2 },
                    ],
                },
                {
                    name: "codZona",
                    type: "select",
                    label: "Zona",
                    placeholder: "Selecciona la zona",
                    required: true,
                    disabled: false,
                    labelVisible: true,
                    visible: true,
                    options: ZONE_OPTIONS,
                },
            ],
        },
    ];

    return (
        <PageLayout
            title={t("users.title")}
            subtitle={t("users.subtitle")}
            actions={[
                ...(canAdd
                    ? [
                          <Button
                              key="add"
                              variant="filled"
                              startIcon={IoMdAdd({})}
                              onClick={() => tableRef.current?.openAddModal()}
                              endIcon={undefined}
                          >
                              {`${t("common.add")} ${t("fields.users")}`}
                          </Button>,
                      ]
                    : []),
            ]}
        >
            <Table
                ref={tableRef}
                hideAddButton={true}
                columns={columnasUsuarios}
                dataTable={usuarios}
                showCheckboxes={false}
                expandable={true}
                expandableBreakpoint={600}
                noResultsMessage={t("users.noResultsFound")}
                actions={[t("common.delete"), t("common.edit")]}
                searchTerms={["nombre", "apellidos", "email", "telefono"]}
                elementName={t("fields.users")}
                keyProp="codUsuario"
                sortProp="nombre"
                loading={loading}
                filterFields={filterFields}
                formElements={formElements}
                editFormElements={editFormElements}
                addApiUrl="/api/Usuario/SetUsuario"
                editApiUrl="/api/Usuario/EditUsuario"
                deleteApiUrl="/api/Usuario/DeleteUsuario"
                userCodAcceso={Number(user.codAcceso)}
                userCodPerfil={Number(user.role)}
                validateForm={(data, mode) => {
                    // En edición no se toca la contraseña.
                    if (mode !== "add") return null;

                    if (data.password !== data.repetirPassword) {
                        return "Las contraseñas no coinciden.";
                    }

                    return validarPassword(data.password);
                }}
                payloadMapper={(payload) => {
                    // El campo de repetición es solo de la UI, no viaja al backend.
                    const { repetirPassword, ...resto } = payload;
                    return resto;
                }}
                excludeFields={["password", "repetirPassword", "passwordHash", "userName", "foto", "notificaciones", "lang", "rememberSession", "codPerfil", "userName", "codAcceso", "codZona", "codIntermediario", "codsProveedor"]}
            />
        </PageLayout>
    );
}
